import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Select, Input, Button, Card } from 'antd';
import { FormComponentProps, ValidationRule } from 'antd/lib/form';
import { WarningTwoTone } from '@ant-design/icons';
import {
  CustomPointOfInterest,
  LocationType
} from '../../../common/api/spidertracks-sdk/types/PointOfInterest';
import { ConfirmationButtons } from '../../common/form';
import { showCustomPoiDeleteModal } from './CustomPoiDeleteModal';
import {
  deleteCustomPois,
  populateCustomPois,
  updateCustomPoi
} from '../../../redux/slice/poiAdmin/thunk';
import { setEditingCustomPoi } from '../../../redux/slice/poiAdmin/poiAdmin';
import {
  getPoiUpdating,
  getEditingCustomPoi,
  getPoiUpdateSuccess,
  getPoiUpdateFailed
} from '../../../redux/selectors/poiAdmin';
import {
  getLocationTypeDisplayName,
  getPoiCodeValidationErrorMessage,
  getPoiNameValidationErrorMessage
} from '../../../util/poi';

const { Option } = Select;

export interface CustomPoiFormData {
  locationId: number;
  code: string;
  name: string;
  type: string;
  latitude: number;
  longitude: number;
  organisationId: string;
  locationTypes: LocationType[];
}

interface ComponentProps extends FormComponentProps {
  initialData: CustomPoiFormData | null;
  onClose: () => void;
}

const LocationTypeOptions = (locationTypes: LocationType[]) =>
  locationTypes
    .map(locationType => {
      return (
        <Option
          key={locationType.id}
          value={`${locationType.description} (${locationType.abbreviation})`}
        >
          {locationType.description} ({locationType.abbreviation})
        </Option>
      );
    })
    .sort((a, b) => a.props.value.localeCompare(b.props.value));

// eslint-disable-next-line react/prop-types
export const CustomPoiEditForm: React.FC<ComponentProps> = ({ initialData, onClose, form }) => {
  const formRef = useRef(form);
  const poiUpdating = useSelector(getPoiUpdating);
  const editingCustomPoi = useSelector(getEditingCustomPoi);
  const poiUpdateSuccess = useSelector(getPoiUpdateSuccess);
  const poiUpdateFailed = useSelector(getPoiUpdateFailed);
  const dispatch = useDispatch();
  const [updateInitiated, setUpdateInitiated] = useState(false);

  useEffect(() => {
    formRef.current = form;
  }, [form]);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    formRef.current.setFieldsValue({
      code: initialData.code,
      name: initialData.name,
      type: initialData.type,
      latitude: initialData.latitude,
      longitude: initialData.longitude
    });
  }, [initialData]);

  useEffect(() => {
    if (initialData && editingCustomPoi) {
      setUpdateInitiated(true);
      dispatch(updateCustomPoi(initialData.organisationId));
    }
  }, [initialData, editingCustomPoi, dispatch]);

  useEffect(() => {
    if (updateInitiated && poiUpdateSuccess && initialData) {
      dispatch(setEditingCustomPoi(undefined));
      setUpdateInitiated(false);
      dispatch(populateCustomPois(initialData.organisationId));
      onClose();
    }
  }, [updateInitiated, poiUpdateSuccess, onClose, initialData, dispatch]);

  useEffect(() => {
    if (updateInitiated && poiUpdateFailed) {
      dispatch(setEditingCustomPoi(undefined));
      setUpdateInitiated(false);
      onClose();
    }
  }, [updateInitiated, poiUpdateFailed, onClose, dispatch]);

  const getEditingCustomPoiFromForm = (
    values: CustomPoiFormData,
    initialData: CustomPoiFormData
  ) => {
    const editingCustomPoi: Partial<CustomPointOfInterest> & { type?: string } = {
      locationId: initialData.locationId
    };

    if (values.code !== initialData.code) {
      editingCustomPoi.code = values.code;
    }

    if (values.name !== initialData.name) {
      editingCustomPoi.name = values.name;
    }

    if (values.type !== initialData.type) {
      const locationTypes = initialData.locationTypes;
      const locationType = locationTypes.find(
        locationType => getLocationTypeDisplayName(locationType) === values.type
      );
      if (locationType) {
        editingCustomPoi.type = locationType.airportType;
      }
    }

    if (values.latitude !== initialData.latitude) {
      editingCustomPoi.latitude = values.latitude;
    }

    if (values.longitude !== initialData.longitude) {
      editingCustomPoi.longitude = values.longitude;
    }

    return editingCustomPoi;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    formRef.current.validateFields((err, values) => {
      if (err) {
        return;
      }

      if (!initialData) {
        return;
      }

      const editingCustomPoi = getEditingCustomPoiFromForm(values, initialData);
      dispatch(setEditingCustomPoi(editingCustomPoi));
    });
  };

  const isFormDirty = formRef.current.isFieldsTouched([
    'code',
    'name',
    'type',
    'latitude',
    'longitude'
  ]);

  const hasValidationErrors = () => {
    const fieldsError = formRef.current.getFieldsError();
    if (!fieldsError) {
      return false;
    }
    const anyFieldHasError = Object.values(fieldsError).some(errors => errors && errors.length > 0);
    return anyFieldHasError;
  };

  const saveDisabled = !isFormDirty || hasValidationErrors();

  const validatePoiCode = (
    _rule: ValidationRule,
    value: string,
    callback: (message?: string) => void
  ) => {
    const errorMessage = getPoiCodeValidationErrorMessage(value);
    if (errorMessage) {
      callback(errorMessage);
    }

    callback();
  };

  const validatePoiName = (
    _rule: ValidationRule,
    value: string,
    callback: (message?: string) => void
  ) => {
    const errorMessage = getPoiNameValidationErrorMessage(value);
    if (errorMessage) {
      callback(errorMessage);
    }

    callback();
  };

  return (
    <div>
      <Form onSubmit={handleSubmit} layout="vertical">
        <Form.Item
          label={
            <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
              <div>Location Code</div>
              <div style={{ color: 'gray' }}> (up to 10 chars)</div>
            </div>
          }
        >
          {formRef.current.getFieldDecorator('code', {
            validateFirst: true,
            rules: [{ validator: validatePoiCode }]
          })(
            <Input
              placeholder="Enter location code"
              onChange={e => {
                const value = e.target.value.toUpperCase();
                e.target.value = value;
                formRef.current.setFieldsValue({ code: value });
                // Trigger validation for this field
                formRef.current.validateFields(['code']);
              }}
            />
          )}
        </Form.Item>
        <Form.Item
          label={
            <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
              <div>Location Name</div>
              <div style={{ color: 'gray' }}> (up to 50 chars)</div>
            </div>
          }
        >
          {formRef.current.getFieldDecorator('name', {
            validateFirst: true,
            rules: [{ validator: validatePoiName }]
          })(<Input placeholder="Enter location name" />)}
        </Form.Item>
        <Form.Item label="Type">
          {formRef.current.getFieldDecorator('type')(
            <Select placeholder="Select location type">
              {LocationTypeOptions(initialData?.locationTypes ?? [])}
            </Select>
          )}
        </Form.Item>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            flexDirection: 'row',
            gap: '2em'
          }}
        >
          <Form.Item label="Latitude">
            {formRef.current.getFieldDecorator('latitude', {
              validateFirst: true,
              rules: [
                {
                  pattern: new RegExp(/^(-)?[0-9]+(.[0-9]+)?$/),
                  message: 'Latitude must be a number'
                },
                {
                  validator: (_rule, value, callback) => {
                    if (!value || value.toString().trim() === '') {
                      callback('Latitude is required');
                    }
                    const numValue = parseFloat(value);
                    if (numValue < -90 || numValue > 90) {
                      callback('Latitude must be between -90 and 90');
                    }
                    callback();
                  }
                }
              ]
            })(<Input placeholder="Enter latitude" />)}
          </Form.Item>
          <Form.Item label="Longitude">
            {formRef.current.getFieldDecorator('longitude', {
              validateFirst: true,
              rules: [
                {
                  pattern: new RegExp(/^(-)?[0-9]+(.[0-9]+)?$/),
                  message: 'Longitude must be a number'
                },
                {
                  validator: (_rule, value, callback) => {
                    if (!value || value.toString().trim() === '') {
                      callback('Longitude is required');
                    }
                    const numValue = parseFloat(value);
                    if (numValue < -180 || numValue > 180) {
                      callback('Longitude must be between -180 and 180');
                    }
                    callback();
                  }
                }
              ]
            })(<Input placeholder="Enter longitude" />)}
          </Form.Item>
        </div>
        <Card
          style={{
            backgroundColor: '#FFFBE6',
            margin: '0',
            padding: '0',
            border: '1px #FFE58F',
            borderRadius: '10px'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'left', flexDirection: 'row' }}>
            <WarningTwoTone
              twoToneColor="#FAAD14"
              style={{ marginRight: '0.25em', color: 'black', fontSize: '1.5em' }}
            />
            <h4>Important</h4>
          </div>
          <p style={{ padding: '0' }}>
            Changing this setting will apply to all previously created events linked to it.
          </p>
        </Card>
        <div style={{ display: 'flex', justifyContent: 'right', marginTop: '1em' }}>
          <Button
            type="danger"
            size="large"
            style={{ width: '85px', margin: '16px 8px 0 0' }}
            hidden={false}
            onClick={() => {
              if (initialData) {
                showCustomPoiDeleteModal({
                  selectedRowKeys: [initialData.locationId.toString()],
                  onDelete: () => {
                    dispatch(deleteCustomPois(initialData.organisationId));
                    onClose();
                  }
                });
              }
            }}
          >
            Delete
          </Button>
          <ConfirmationButtons isSaving={poiUpdating} onClose={onClose} disabled={saveDisabled} />
        </div>
      </Form>
    </div>
  );
};
